import React, { useState } from 'react';
import { View } from 'react-native';
import styled from '@2pulse/responsive';
import { DateTime } from 'luxon';
import { FilterButton, Panel } from '@app/components';
import { StatusDot } from '@app/components/decor/StatusDot';
import { extractApiGenericError, normalize } from '@app/helpers';
import { useCheckPermissions } from '@app/hooks/useCheckPermissions';
import { useToast } from '@app/hooks';
import { fiscalApi } from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import type { IFiscalArchiveSummary } from '@tickeat/types';
import { COULEUR_ETAT } from './couleurs';
import { DOCUMENTATION_CONSERVATION_MD } from './documentation-conservation';
import { FiscalArchiveCard } from './FiscalArchiveCard';
import { detailDeVerification, type LigneDeVerification } from './fiscal.logic';

// Archives d'une caisse (6.9). L'encart de tête rappelle l'obligation de
// conservation sur support externe (6.9.2.5) et donne la clé publique de
// l'engine, celle qui signe les archives (6.9.4.3).
// `enTete` : ce que la page pose AVANT les archives (les commandes
// d'entraînement, revue de la tâche 9). Rendu ici et pas dans l'écran parce que
// la colonne de droite est cette vue-ci.
//
// Cette colonne est TRANSLATÉE HORS DE L'ÉCRAN tant que le triptyque est fermé
// (`Triptych.tsx`) : contrairement à ce que disait ce commentaire, l'en-tête
// n'est PAS visible sans caisse sélectionnée (revue finale 9c-B, I2). Le panneau
// des commandes d'entraînement est donc AUSSI rendu dans l'état vide de
// `FiscalRegisterCard` ; ici, il sert à qui travaille caisse ouverte.
// `.toUTC()` avant `.toFormat` (fix round 1, M4) : sans elle, un fuseau a l'ouest de
// Greenwich fait reculer la date affichee d'un jour (minuit UTC devient la veille en local).
const dateFr = (iso: string | null) => (iso ? DateTime.fromISO(iso).toUTC().toFormat('dd/MM/yyyy') : '—');

export const FiscalArchives = ({ registerId, enTete }: { registerId?: string; enTete?: React.ReactNode }) => {
  const toast = useToast();
  const peutGerer = useCheckPermissions()(['dashboard::fiscal.manage']);
  // Sans boutique, la gateway n'a pas d'en-tête `x-shop` : on ne demande rien
  // plutôt que d'afficher « Bad Request Exception » (retour du 9 sept.).
  const boutique = useAppSelector((state) => state.app.selectedShop);
  const { data: archives = [], isFetching } = fiscalApi.useReadFiscalArchivesQuery(registerId ?? '', { skip: !registerId || !boutique });
  // `isError` compte AUTANT que `publicKeyPem` nul : une lecture du trousseau
  // en échec laissait la carte muette sur la clé, sans dire pourquoi.
  const { data: cles, isError: clesEnEchec } = fiscalApi.useReadFiscalKeysQuery(undefined, { skip: !boutique });
  // I1(b) (revue round 1, E-38) : `keys` republie l'UNION des trousseaux déclarés
  // (`FISCAL_ARCHIVE_RETIRED_KEYS`) et de ceux retrouvés dans les archives déjà signées —
  // un trousseau DÉCOUVERT n'a jamais de fenêtre de validité connue (M3 l'impose pour tout
  // trousseau déclaré, `null`/`null` ne peut donc venir que de la découverte). C'est ce qui
  // distingue une rotation correctement documentée d'une rotation qui a oublié de recopier
  // la clé sortante — sans re-router cette logique par le serveur.
  const trousseauxOublies = (cles?.keys ?? []).filter((k) => k.keyId !== cles?.current.keyId && k.notBefore === null && k.notAfter === null);
  const [telecharger] = fiscalApi.useDownloadFiscalArchiveMutation();
  const [verifier] = fiscalApi.useVerifyFiscalArchiveMutation();
  const [relancer] = fiscalApi.useRetryFiscalArchiveMutation();
  // 6.9.4.2 (Task 8, E-37) : chargée dès l'ouverture de la carte, jamais
  // seulement à la demande — sans boutique ni archive, aucune autre donnée de
  // cette carte n'a besoin d'exister pour que la documentation le soit.
  const { data: documentation } = fiscalApi.useLireDocumentationArchiveQuery();
  const [enCours, setEnCours] = useState<{ id: string; action: 'telecharger' | 'verifier' | 'relancer' } | null>(null);
  const [rapport, setRapport] = useState<{ id: string; titre: string; ok: boolean; lignes: LigneDeVerification[] } | null>(null);

  const agir = async (a: IFiscalArchiveSummary, action: 'telecharger' | 'verifier' | 'relancer') => {
    setEnCours({ id: a._id, action });
    try {
      if (action === 'telecharger') await telecharger(a._id).unwrap();
      if (action === 'verifier') { const r = await verifier(a._id).unwrap(); setRapport({ id: a._id, ...detailDeVerification(r) }); }
      if (action === 'relancer') { await relancer({ archiveId: a._id, registerId: a.registerId }).unwrap(); toast.success('Constitution relancée.'); }
    } catch (e: any) {
      toast.error(extractApiGenericError(e) ?? 'L’action a échoué.');
    } finally {
      setEnCours(null);
    }
  };

  const copier = async (valeur: string, succes: string) => {
    try { await navigator.clipboard.writeText(valeur); toast.success(succes); }
    catch { toast.error('Impossible de copier.'); }
  };

  // 6.9.4.2 (Task 8, E-37) : « la documentation doit pouvoir être consultée à
  // tout instant ». SYNCHRONE, volontairement — pas d'`await` entre le clic et
  // `window.open` : c'est ce qui fait que Safari rattache l'ouverture au
  // geste de l'utilisateur (même contrainte que `suivreArchiveTelechargee`
  // dans `store/services/fiscal.ts`, qui documente le blocage inverse). La
  // requête est donc chargée eagerly ci-dessus, jamais déclenchée ici.
  const ouvrirLaDocumentation = () => {
    if (!documentation) { toast.error('Documentation indisponible pour le moment.'); return; }
    const blob = new Blob([documentation.content], { type: documentation.contentType });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  // 6.9.2.5 (Task 18, E-57) : ouvre la DOCUMENTATION UTILISATEUR de l'obligation
  // de conservation — pas la documentation technique de l'archive ci-dessus.
  // Contenu local (voir `DOCUMENTATION_CONSERVATION_MD`) : aucune requête,
  // toujours disponible, même hors ligne.
  const ouvrirLaDocumentationConservation = () => {
    const blob = new Blob([DOCUMENTATION_CONSERVATION_MD], { type: 'text/markdown; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  // MÊME ENVELOPPE QUE LA COLONNE DE DROITE DES SERVICES : `Panel.FormContainer`
  // porte le défilement et les marges (55 / 55 / 144). Une `ScrollView` nue prenait
  // la largeur de son contenu et débordait de la fenêtre par la droite.
  //
  // La colonne, elle, ne DÉCLARE pas sa largeur : elle prend ce que le triptyque lui
  // laisse (`flexBasis: 0` + `flexGrow: 1`) et réserve la barre latérale en marge.
  // `Panel.Content` posait `theme.sizes.layoutForm`, figé au chargement de la page :
  // additionné à la largeur de la fiche, le total pouvait dépasser ce que la fenêtre
  // expose et pousser la fiche sous la barre latérale. Ici la colonne cède toujours —
  // `minWidth: 0` — plutôt que d'élargir la ligne.
  return (
    <Colonne>
      <Panel.FormContainer>
        {enTete}

        <Carte>
          <CarteTitre>Vos archives fiscales</CarteTitre>
          <CarteTexte>
            Conservez-les sur un support externe sécurisé : la loi vous impose de les garder pendant six années ou exercices, plus l’année ou l’exercice en cours.
            Chaque archive est signée par Tickeat ; sa clé publique permet d’en vérifier l’authenticité sans le logiciel.
          </CarteTexte>

          {/* 6.9.4.2 (Task 8, E-37) : la documentation de l'archive doit rester
              consultable à tout instant, y compris avant qu'une seule archive
              n'existe — le lien n'est donc conditionné ni à `registerId` ni à
              `archives`. */}
          <FilterButton
            title="Lire la documentation de l’archive"
            onPress={ouvrirLaDocumentation}
            testID="ouvrir-documentation-archive" />

          {/* 6.9.2.5 (Task 18, E-57) : la documentation UTILISATEUR de l'obligation
              de conservation — distincte de la documentation technique de l'archive
              ci-dessus. Copie complète : `docs/nf525/documentation-utilisateur-conservation.md`. */}
          <FilterButton
            title="Lire la documentation de conservation"
            onPress={ouvrirLaDocumentationConservation}
            testID="lien-documentation-conservation" />

          {/* I1(c) (revue round 2, E-38) : au moins une archive a été signée par un trousseau
              qu'aucune rotation documentée (`FISCAL_ARCHIVE_RETIRED_KEYS`) ne mentionne. La
              clé reste publiée ci-dessous (I1b) donc l'archive reste vérifiable, mais
              l'exploitation doit régulariser — c'est aussi journalisé côté engine, à chaque
              lecture (`FiscalHandler.trousseauxPublies`, `logger.error`) : la contre-
              vérification ne peut pas vivre dans un hook de démarrage, `FiscalHandler` étant
              REQUEST-scoped par transitivité (tenant), jamais instancié hors requête. */}
          {trousseauxOublies.length > 0 && (
            <Bandeau ok={false} testID="alerte-trousseau-oublie">
              <BandeauTexte ok={false}>
                {trousseauxOublies.length === 1
                  ? `Le trousseau ${trousseauxOublies[0].keyId} a signé une archive sans figurer dans l’historique d’exploitation.`
                  : `${trousseauxOublies.length} trousseaux ont signé des archives sans figurer dans l’historique d’exploitation.`}
                {' '}Contactez Tickeat pour régulariser la fenêtre de validité.
              </BandeauTexte>
            </Bandeau>
          )}

          {/* L'engine peut tourner sans trousseau (environnement de recette), et
              la lecture du trousseau peut échouer : dans les deux cas on le DIT,
              plutôt que d'offrir un bouton « Copier » inerte ou de se taire. */}
          {cles?.current.publicKeyPem ? (
            <LigneCle>
              <CarteTexte>Clé publique de signature Tickeat ({cles.current.keyId})</CarteTexte>
              <FilterButton title="Copier" onPress={() => copier(cles.current.publicKeyPem as string, 'Clé publique de l’engine copiée.')} testID="copier-cle-engine" />
            </LigneCle>
          ) : (cles || clesEnEchec) ? (
            <Discret testID="cle-engine-absente">Clé de signature non configurée sur cet environnement.</Discret>
          ) : null}

          {/* Trousseaux RETIRÉS (6.9.4.3, E-38) : une archive signée avant une
              rotation reste vérifiable sans devoir réclamer la clé à Tickeat —
              l'historique complet (courant inclus) est publié par `readKeys`,
              on n'affiche ici que ce qui n'est plus le trousseau courant. */}
          {(cles?.keys ?? []).filter((k) => k.keyId !== cles?.current.keyId).length > 0 && (
            <TrousseauxRetires testID="trousseaux-retires">
              <CarteTexte>Trousseaux retirés (archives signées avant une rotation)</CarteTexte>
              {(cles?.keys ?? [])
                .filter((k) => k.keyId !== cles?.current.keyId)
                .map((k) => (
                  <LigneCle key={k.keyId} testID={`trousseau-retire-${k.keyId}`}>
                    <TrousseauInfos>
                      <CarteTexte>{k.keyId}</CarteTexte>
                      <Discret testID={`trousseau-retire-validite-${k.keyId}`}>
                        Du {dateFr(k.notBefore)} au {dateFr(k.notAfter)}
                      </Discret>
                    </TrousseauInfos>
                    {k.publicKeyPem && (
                      <FilterButton
                        title="Copier"
                        onPress={() => copier(k.publicKeyPem as string, `Clé publique du trousseau ${k.keyId} copiée.`)}
                        testID={`copier-cle-${k.keyId}`}
                      />
                    )}
                  </LigneCle>
                ))}
            </TrousseauxRetires>
          )}
        </Carte>

        {!registerId && <Discret>Sélectionnez une caisse pour voir ses archives.</Discret>}
        {registerId && !isFetching && archives.length === 0 && (
          <Discret>Aucune archive pour cette caisse : la première est constituée à la clôture de l’exercice.</Discret>
        )}

        {[...archives].sort((a, b) => b.sequence - a.sequence).map((a) => (
          <View key={a._id}>
            <FiscalArchiveCard
              archive={a}
              peutGerer={peutGerer}
              enCours={enCours?.id === a._id ? enCours.action : null}
              onTelecharger={(x) => agir(x, 'telecharger')}
              onVerifier={(x) => agir(x, 'verifier')}
              onRelancer={(x) => agir(x, 'relancer')}
              onCopierEmpreinte={(x) => copier(x.sha256 ?? '', 'Empreinte copiée.')} />
            {rapport?.id === a._id && (
              <Rapport testID="rapport-archive">
                <Bandeau ok={rapport.ok}><BandeauTexte ok={rapport.ok}>{rapport.titre}</BandeauTexte></Bandeau>
                {rapport.lignes.map((l) => (
                  <LigneRapport key={l.cle}>
                    <PastilleHolder><StatusDot color={l.ok ? COULEUR_ETAT.vert : COULEUR_ETAT.rouge} /></PastilleHolder>
                    <LigneCorps>
                      <LigneTitre>{l.libelle}</LigneTitre>
                      <LigneDetail ok={l.ok}>{l.detail}</LigneDetail>
                    </LigneCorps>
                  </LigneRapport>
                ))}
              </Rapport>
            )}
          </View>
        ))}
      </Panel.FormContainer>
    </Colonne>
  );
};

// La colonne de droite du triptyque : tout l'espace restant après la fiche,
// moins la largeur de la barre latérale, que la fenêtre translatée recouvre.
const Colonne = styled.View(({ theme }) => ({
  height: '100%',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  minWidth: 0,
  marginRight: theme.sizes.sidebarWidth,
}));

// Même carte que les fiches de commande (`OrderCardContainer`) : plus de
// bordure jaune étirée jusqu'au bord de la colonne.
const Carte = styled.View(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(42),
  padding: normalize(21),
  marginBottom: normalize(21),
  rowGap: normalize(10),
}));

const CarteTitre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  color: theme.colors.babypowder,
}));

const CarteTexte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  lineHeight: normalize(23),
  color: theme.colors.babypowder,
  flexShrink: 1,
}));

const LigneCle = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: normalize(13),
});

const TrousseauxRetires = styled.View({
  rowGap: normalize(10),
  paddingTop: normalize(5),
});

const TrousseauInfos = styled.View({
  rowGap: normalize(2),
});

const Discret = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  lineHeight: normalize(23),
  color: theme.colors.grey,
  paddingBottom: normalize(13),
}));

const Rapport = styled.View({
  paddingHorizontal: normalize(21),
  paddingBottom: normalize(21),
  rowGap: normalize(10),
});

const Bandeau = styled.View<{ ok: boolean }>(({ props }) => ({
  width: '100%',
  borderRadius: normalize(42),
  paddingVertical: normalize(10),
  paddingHorizontal: normalize(21),
  backgroundColor: props.ok ? 'rgba(42, 168, 49, 0.18)' : 'rgba(220, 0, 0, 0.18)',
  borderWidth: 1,
  borderColor: props.ok ? COULEUR_ETAT.vert : COULEUR_ETAT.rouge,
}));

const BandeauTexte = styled.Text<{ ok: boolean }>(({ props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  textAlign: 'center',
  color: props.ok ? COULEUR_ETAT.vert : COULEUR_ETAT.rouge,
}));

const LigneRapport = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  columnGap: normalize(13),
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(20),
  paddingVertical: normalize(13),
  paddingHorizontal: normalize(18),
}));

const PastilleHolder = styled.View({ paddingTop: normalize(5) });

const LigneCorps = styled.View({ flexShrink: 1, rowGap: normalize(2) });

const LigneTitre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '600',
  color: theme.colors.babypowder,
}));

const LigneDetail = styled.Text<{ ok: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: props.ok ? theme.colors.grey : COULEUR_ETAT.rouge,
}));
